/**
 * Permission Service for Admin Frontend
 * 
 * Provides centralized permission management for the Vue admin application.
 * Integrates with the backend unified permission system.
 */

import axios from '@/services/axios'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'

class PermissionService {
  constructor() {
    this.permissions = []
    this.loading = false
    this.lastLoaded = null
    this.cacheTimeout = 5 * 60 * 1000 // 5 minutes
  }

  /**
   * Load user permissions from backend
   */
  async loadUserPermissions(forceRefresh = false) {
    try {
      // Check cache
      if (!forceRefresh && this.permissions.length > 0 && this.lastLoaded) {
        const timeSinceLoaded = Date.now() - this.lastLoaded
        if (timeSinceLoaded < this.cacheTimeout) {
          console.log('📋 Permission Service: Using cached permissions')
          return this.permissions
        }
      }

      console.log('📋 Permission Service: Loading permissions from server...')
      this.loading = true

      const response = await this.getMyPermissions()
      
      if (response.permissions) {
        this.permissions = response.permissions || []
        this.lastLoaded = Date.now()
        
        console.log(`   - Loaded ${this.permissions.length} permissions:`)
        this.permissions.forEach(permission => {
          console.log(`     • ${permission}`)
        })
        
        // Update auth store
        const authStore = useAuthStore()
        if (authStore.user) {
          authStore.user.permissions = this.permissions
          // Save to localStorage
          localStorage.setItem('user', JSON.stringify(authStore.user))
        }
        
        return this.permissions
      } else {
        throw new Error('Failed to load permissions')
      }
    } catch (error) {
      console.error('❌ Permission Service: Failed to load permissions:', error)
      this.permissions = []
      throw error
    } finally {
      this.loading = false
    }
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission) {
    if (!permission) return true
    
    const authStore = useAuthStore()
    
    // Super admin bypass
    if (this.isSuperAdmin()) {
      return true
    }
    
    // Admin role bypass for admin permissions
    if (this.isAdmin() && permission.startsWith('admin_')) {
      return true
    }
    
    // Check permissions array
    const userPermissions = authStore.userPermissions
    const hasPermission = userPermissions.includes(permission)
    
    console.log(`🔐 Permission Check: ${permission} = ${hasPermission ? '✅' : '❌'}`)
    
    return hasPermission
  }

  /**
   * Check multiple permissions (requires all)
   */
  hasAllPermissions(permissions) {
    return permissions.every(permission => this.hasPermission(permission))
  }

  /**
   * Check multiple permissions (requires any)
   */
  hasAnyPermission(permissions) {
    return permissions.some(permission => this.hasPermission(permission))
  }

  /**
   * Check if user is super admin
   */
  isSuperAdmin() {
    const authStore = useAuthStore()
    return ['super_admin', 'owner'].includes(authStore.userRole)
  }

  /**
   * Check if user is admin
   */
  isAdmin() {
    const authStore = useAuthStore()
    return ['admin', 'owner', 'super_admin', 'manager'].includes(authStore.userRole)
  }

  /**
   * Get permissions by category
   */
  getPermissionsByCategory() {
    const categories = {}
    
    this.permissions.forEach(permission => {
      const parts = permission.split('.')
      const resource = parts[0]
      const action = parts[1]
      
      if (!categories[resource]) {
        categories[resource] = []
      }
      
      categories[resource].push({
        code: permission,
        resource,
        action,
        description: this.getPermissionDescription(permission)
      })
    })
    
    return categories
  }

  /**
   * Get human-readable permission description
   */
  getPermissionDescription(permission) {
    const descriptions = {
      // Admin permissions
      'admin_users.view': 'View admin users',
      'admin_users.create': 'Create admin users',
      'admin_users.update': 'Update admin users',
      'admin_users.delete': 'Delete admin users',
      'admin_system.manage': 'Manage system settings',
      'admin_security.audit': 'View security audit logs',
      'admin_permissions.manage': 'Manage user permissions',
      
      // Content permissions
      'content.view': 'View content',
      'content.create': 'Create content',
      'content.update': 'Update content',
      'content.delete': 'Delete content',
      'content.publish': 'Publish content',
      'content.approve': 'Approve content',
      'content.manage': 'Full content management',
      'content.publish_featured': 'Publish featured content',
      'content.moderate': 'Moderate content',
      'content.analytics': 'View content analytics',
      
      // Product permissions
      'products.view': 'View products',
      'products.create': 'Create products',
      'products.update': 'Update products',
      'products.delete': 'Delete products',
      
      // B2B permissions
      'b2b_dashboard.access': 'Access B2B dashboard',
      'b2b_clients.view': 'View B2B clients',
      'b2b_clients.create': 'Create B2B clients',
      'b2b_clients.update': 'Update B2B clients',
      'b2b_clients.delete': 'Delete B2B clients',
      'b2b_orders.view_all': 'View all B2B orders',
      'b2b_orders.create': 'Create B2B orders',
      'b2b_orders.manage': 'Manage B2B orders',
      
      // System permissions
      'system.backup': 'Perform system backups',
      'system.deploy': 'Deploy system updates',
      'system.monitor': 'Monitor system health',
      
      // Analytics permissions
      'analytics.view': 'View analytics',
      'analytics.export': 'Export analytics data'
    }
    
    return descriptions[permission] || permission.replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  /**
   * Clear cached permissions
   */
  clearCache() {
    this.permissions = []
    this.lastLoaded = null
    console.log('📋 Permission Service: Cache cleared')
  }

  /**
   * Get current permissions (from cache)
   */
  getCurrentPermissions() {
    return this.permissions
  }

  /**
   * Check if permissions are loaded
   */
  arePermissionsLoaded() {
    return this.permissions.length > 0 && this.lastLoaded !== null
  }

  // === BACKEND API METHODS ===

  // Get all permissions grouped by category
  async getPermissionCategories() {
    const response = await axios.get('/v1/admin/permissions/categories');
    return response.data.data;
  }

  // Get specific user's permissions
  async getUserPermissions(userId) {
    const response = await axios.get(`/v1/admin/permissions/user/${userId}`);
    return response.data.data;
  }

  // Get current user's permissions
  async getMyPermissions() {
    const response = await axios.get('/v1/admin/permissions/my-permissions');
    return response.data.data;
  }

  // Grant permission
  async grantPermission(userId, permissionCode, options = {}) {
    const response = await axios.post('/v1/admin/permissions/grant', {
      userId,
      permissionCode,
      ...options
    });
    return response.data;
  }

  // Revoke permission
  async revokePermission(userId, permissionCode, reason) {
    const response = await axios.post('/v1/admin/permissions/revoke', {
      userId,
      permissionCode,
      reason
    });
    return response.data;
  }

  // Bulk update permissions
  async bulkUpdatePermissions(userId, permissions, reason) {
    const response = await axios.put(`/v1/admin/permissions/user/${userId}/bulk`, {
      permissions,
      reason
    });
    return response.data;
  }

  async getRolePermissions(role) {
    const response = await axios.get(`/v1/admin/permissions/role/${role}`);
    return response.data.data;
  }

  // Get permission history
  async getUserPermissionHistory(userId) {
    const response = await axios.get(`/v1/admin/permissions/user/${userId}/history`);
    return response.data.data;
  }
}

// Create singleton instance
const permissionService = new PermissionService()

// Vue 3 plugin
export const PermissionPlugin = {
  install(app) {
    app.config.globalProperties.$permissions = permissionService
    app.provide('permissions', permissionService)
  }
}

// Export both named and default
export { permissionService }
export default permissionService